import { useState, useCallback } from 'react';
import { notificationsService } from '../services/notificationsService';
import { getCurrentYear } from '../utils/eventStatus';

const INITIAL_FILTERS = {
  status: '',
  deliveryStatus: '',
  type: '',
  recipientPhone: '',
  startDate: '',
  endDate: '',
  year: getCurrentYear()
};

const INITIAL_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
  from: 0,
  to: 0,
  currentPage: 1,
  lastPage: 1
};

// Quitar valores vacíos antes de llamar a la API (forbidNonWhitelisted)
const cleanParams = (params) =>
  Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value.toString().trim() !== '') {
      acc[key] = typeof value === 'string' ? value.trim() : value;
    }
    return acc;
  }, {});

/**
 * Hook para monitorear los mensajes de WhatsApp: listado, estadísticas,
 * filtros, paginación y reintento de fallidos.
 */
export const useNotificationsManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);

  // Las estadísticas usan los mismos filtros que el listado (sin paginación)
  const fetchStats = useCallback(
    async (overrideFilters = null) => {
    setStatsLoading(true);
    try {
      setStats(await notificationsService.getStats(cleanParams(overrideFilters || filters)));
    } catch (err) {
      // Las estadísticas son auxiliares: si fallan, el listado sigue usable
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
    },
    [filters]
  );

  const fetchNotifications = useCallback(
    async (overrideFilters = null, overridePage = null) => {
      setLoading(true);
      setError(null);

      try {
        const activeFilters = overrideFilters || filters;
        const page = overridePage || pagination.page;

        const response = await notificationsService.getNotifications(
          cleanParams({ ...activeFilters, page, limit: pagination.limit })
        );
        const meta = response.pagination || {};

        setNotifications(Array.isArray(response.data) ? response.data : []);
        setPagination((prev) => ({
          ...prev,
          page: meta.page ?? page,
          total: meta.total ?? 0,
          totalPages: meta.totalPages ?? 0,
          from: meta.total ? meta.from ?? 0 : 0,
          to: meta.to ?? 0,
          currentPage: meta.page ?? page,
          lastPage: meta.totalPages || 1
        }));
      } catch (err) {
        setError(err.message);
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.page, pagination.limit]
  );

  const refresh = useCallback(async () => {
    await Promise.all([fetchNotifications(), fetchStats()]);
  }, [fetchNotifications, fetchStats]);

  // Actualizar filtros sin disparar la búsqueda
  const applyFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Aplicar filtros y buscar desde la página 1
  const applyFiltersNow = useCallback(async () => {
    setPagination((prev) => ({ ...prev, page: 1, currentPage: 1 }));
    await Promise.all([fetchNotifications(filters, 1), fetchStats(filters)]);
  }, [filters, fetchNotifications, fetchStats]);

  // Cambiar el año aplica de inmediato (listado y estadísticas)
  const changeYear = useCallback(
    async (year) => {
      const nextFilters = { ...filters, year };
      setFilters(nextFilters);
      setPagination((prev) => ({ ...prev, page: 1, currentPage: 1 }));
      await Promise.all([fetchNotifications(nextFilters, 1), fetchStats(nextFilters)]);
    },
    [filters, fetchNotifications, fetchStats]
  );

  const changePage = useCallback(
    async (newPage) => {
      setPagination((prev) => ({ ...prev, page: newPage, currentPage: newPage }));
      await fetchNotifications(null, newPage);
    },
    [fetchNotifications]
  );

  const clearFilters = useCallback(async () => {
    setFilters(INITIAL_FILTERS);
    setPagination((prev) => ({ ...prev, page: 1, currentPage: 1 }));
    await Promise.all([fetchNotifications(INITIAL_FILTERS, 1), fetchStats(INITIAL_FILTERS)]);
  }, [fetchNotifications, fetchStats]);

  const retryFailed = useCallback(async () => {
    setRetrying(true);
    setActionMessage(null);
    try {
      const result = await notificationsService.retryFailed();
      setActionMessage({
        type: 'success',
        text: `${result.retried} mensaje(s) marcados para reintento${
          result.skipped ? `, ${result.skipped} omitidos por superar el máximo de reintentos` : ''
        }.`
      });
      await Promise.all([fetchNotifications(), fetchStats()]);
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setRetrying(false);
    }
  }, [fetchNotifications, fetchStats]);

  // Tras una acción masiva (reenvío, corrección de teléfono): avisa y recarga listado y tarjetas
  const completeAction = useCallback(
    async (text) => {
      setActionMessage({ type: 'success', text });
      await Promise.all([fetchNotifications(), fetchStats()]);
    },
    [fetchNotifications, fetchStats]
  );

  return {
    notifications,
    stats,
    loading,
    statsLoading,
    retrying,
    error,
    actionMessage,
    filters,
    pagination,
    fetchNotifications,
    fetchStats,
    refresh,
    applyFilters,
    applyFiltersNow,
    changeYear,
    changePage,
    clearFilters,
    retryFailed,
    completeAction
  };
};
